import React, { createContext, useContext, useCallback } from 'react';
import { AlertCircle, CheckCircle2, XCircle, X, AlertTriangle } from 'lucide-react';

const AlertContext = createContext(null);

const alertStyles = {
  success: {
    backgroundColor: 'bg-emerald-50',
    borderColor: 'border-emerald-500',
    textColor: 'text-emerald-800',
    iconColor: 'text-emerald-500',
    icon: CheckCircle2,
    progressBarColor: 'bg-emerald-500',
  },
  error: {
    backgroundColor: 'bg-red-50',
    borderColor: 'border-red-500',
    textColor: 'text-red-800',
    iconColor: 'text-red-500',
    icon: XCircle,
    progressBarColor: 'bg-red-500',
  },
  warning: {
    backgroundColor: 'bg-amber-50',
    borderColor: 'border-amber-500',
    textColor: 'text-amber-800',
    iconColor: 'text-amber-500',
    icon: AlertTriangle,
    progressBarColor: 'bg-amber-500',
  },
  info: {
    backgroundColor: 'bg-blue-50',
    borderColor: 'border-blue-500',
    textColor: 'text-blue-800',
    iconColor: 'text-blue-500',
    icon: AlertCircle,
    progressBarColor: 'bg-blue-500',
  },
};

const Alert = ({ message, type, id }) => {
  const { removeAlert } = useContext(AlertContext);
  const style = alertStyles[type] || alertStyles.info;
  const Icon = style.icon;

  React.useEffect(() => {
    const timer = setTimeout(() => {
      removeAlert(id);
    }, 5000);

    return () => clearTimeout(timer);
  }, [id, removeAlert]);

  return (
    <div className="animate-fade-in" style={{
        zIndex:1200
    }}>
      <div 
        className={`
          relative flex items-center gap-3 p-4 pr-12 rounded-lg border
          ${style.backgroundColor}
          ${style.borderColor}
          shadow-lg
          min-w-[320px]
          max-w-[480px]
        `}
      >
        <Icon className={`w-5 h-5 ${style.iconColor}`} />
        
        <p className={`text-sm font-medium ${style.textColor}`}>
          {message}
        </p>

        <button 
          onClick={() => removeAlert(id)}
          className={`
            absolute right-4 top-1
            hover:bg-black/5 rounded-full p-1
            transition-colors duration-200
          `}
        >
          <X className={`w-4 h-4 ${style.iconColor}`} />
        </button>

        {/* Progress bar */}
        <div className="absolute bottom-0 left-0 w-full h-1 bg-black/5">
          <div 
            className={`h-full ${style.progressBarColor}`}
            style={{
              width: '100%',
              animation: 'shrink 5000ms linear forwards',
            }}
          />
        </div>
      </div>
    </div>
  );
};

export const AlertProvider = ({ children }) => {
  const [alerts, setAlerts] = React.useState([]);

  const removeAlert = useCallback((id) => {
    setAlerts(prev => prev.filter(alert => alert.id !== id));
  }, []);

  const addAlert = useCallback((message, type = 'info') => {
    const id = Date.now();
    setAlerts(prev => [...prev, { id, message, type }]);
  }, []);

  return (
    <AlertContext.Provider value={{ addAlert, removeAlert }}>
      {children}
      <div className="fixed top-4 right-4 z-50 flex flex-col gap-2">
        {alerts.map(alert => (
          <Alert
            key={alert.id}
            id={alert.id}
            message={alert.message}
            type={alert.type}
          />
        ))}
      </div>
      {/* <style jsx>{`
        @keyframes shrink {
          from { width: 100%; }
          to { width: 0%; }
        }

        @keyframes fade-in {
          from { 
            opacity: 0;
            transform: translateY(-1rem);
          }
          to { 
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in {
          animation: fade-in 0.2s ease-out;
        }
      `}</style> */}
      <style>{`
  @keyframes shrink {
    from { width: 100%; }
    to { width: 0%; }
  }

  @keyframes fade-in {
    from { 
      opacity: 0;
      transform: translateY(-1rem);
    }
    to { 
      opacity: 1;
      transform: translateY(0);
    }
  }

  .animate-fade-in {
    animation: fade-in 0.2s ease-out;
  }
`}</style>

    </AlertContext.Provider>
  );
};

// Custom hook to use alerts
export const useAlert = () => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error('useAlert must be used within an AlertProvider');
  }
  return context;
};